import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useApi } from '@/shared/api';

/**
 * Die Datenverdrahtung des Apartment-Modus: Belegung plus die Apartment-Stammdaten, aus denen
 * Filterliste, Adresszeile und Währung kommen.
 *
 * `enabled` statt eines Aufruf-Verzichts, damit der Stellplatz-Modus nicht die Belegung nachlädt,
 * die er gar nicht zeigt — und beim Zurückschalten trotzdem sofort wieder da ist.
 */
export function useApartmentCalendar(
  from: string,
  to: string,
  propertyIds: string[] | null,
  enabled: boolean,
) {
  const api = useApi();

  const calendar = useQuery({
    queryKey: ['calendar', from, to, propertyIds],
    queryFn: () => api.calendar.getRange(from, to, propertyIds),
    refetchOnWindowFocus: true,
    enabled,
  });

  const properties = useQuery({
    queryKey: ['properties', 'list-for-calendar', propertyIds],
    // Alle Lifecycle-Zustände — sonst fehlt die Währung eines archivierten Apartments und sein
    // Name im Filter, obwohl seine Zeilen sichtbar sein können.
    queryFn: () =>
      api.properties.list({
        pageSize: 200,
        propertyIds: propertyIds ?? undefined,
        lifecycle: 'All',
      }),
    staleTime: 5 * 60_000,
  });

  // Ohne Stammdaten (noch nicht da oder Fehler) tragen die Zeilen der Belegung Namen und Währung –
  // sonst stünde „Alle Apartments (0)“ über einem vollen Raster.
  const propertyOptions = useMemo(
    () =>
      properties.data
        ? properties.data.items
            .filter((property) => propertyIds === null || propertyIds.includes(property.id))
            .map((property) => ({
              id: property.id,
              name: property.adminDisplayName ?? property.name,
            }))
        : (calendar.data?.properties ?? []).map((row) => ({
            id: row.propertyId,
            name: row.propertyName,
          })),
    [properties.data, propertyIds, calendar.data],
  );

  /** Address-Lookup für die Property-Sidebar (Zeile 2). */
  const addressByPropertyId = useMemo(() => {
    const map = new Map<string, string>();
    for (const property of properties.data?.items ?? []) {
      const parts = [property.address?.street, property.address?.city].filter(Boolean);
      if (parts.length > 0) map.set(property.id, parts.join(' · '));
    }
    return map;
  }, [properties.data]);

  const currencyForProperty = (propertyId: string): string =>
    calendar.data?.properties.find((row) => row.propertyId === propertyId)?.currency ??
    properties.data?.items.find((property) => property.id === propertyId)?.currency ??
    'EUR';

  return {
    data: calendar.data,
    isLoading: calendar.isLoading,
    error: calendar.error,
    isFetching: calendar.isFetching,
    refetch: () => void calendar.refetch(),
    propertyOptions,
    addressByPropertyId,
    currencyForProperty,
  };
}
